import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UserRoleEnum } from 'src/app/common/services/auth/enums/user-role.enum';
import { StorageService } from 'src/app/common/services/storage/storage.service';
import { StatusEnum } from 'src/app/common/utils/status-enum.models';
import { markAllControlsAsDirty } from 'src/app/form-helper.ts';
import { ButtonColorEnum } from 'src/app/shared/buttons/enums/button-color.enum';
import { InputTypeEnum } from 'src/app/shared/input/enums/input-type.enum';
import { MaskTypeEnum } from 'src/app/shared/input/enums/mask-type.enum';
import {
  GetUserRequest,
  GetUserResponse,
  UpdateUserRequest,
} from '../../service/models/user.service.model';
import { UserService } from '../../service/user.service';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
})
export class UserDetailsComponent {
  loadingGet: boolean = false;
  loadingUpdate: boolean = false;

  loadingSupervisors: boolean = false;
  loadingSearchSupervisors: boolean = false;
  totalItemsSupervisors: number = 0;
  loadingMoreSupervisors: boolean = false;

  supervisorsFilter: GetUserRequest = {
    pageSize: 10,
    pageNumber: 1,
    profiles: ['SUPERVISOR'],
    status: [StatusEnum.ATIVO],
    sortAscending: true,
    sortBy: 'Name',
  } as GetUserRequest;

  error: boolean = false;
  userId: string = '';
  selectSupervisorEnabled: boolean = true;
  activeOptions: string[] = ['Ativo', 'Inativo'];
  roleOptions: string[] = [];
  supervisors: string[] = [];
  originalSupervisors: GetUserResponse[] = [];
  selectedSupervisors: string[] = [];

  form = this.formBuilder.group({
    name: ['', Validators.required],
    email: ['', Validators.required],
    functional: ['', Validators.required],
    supervisor: '',
    active: ['', Validators.required],
    perfil: ['', Validators.required],
    recorderId: [''],
  });

  ButtonColorEnum = ButtonColorEnum;
  MaskTypeEnum = MaskTypeEnum;
  InputTypeEnum = InputTypeEnum;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly userService: UserService,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly storageService: StorageService
  ) {}

  ngOnInit() {
    this.route.params.subscribe((params) => {
      const userIdFromUrl = params['id'];

      this.userId = userIdFromUrl;
      this.loadUserData();
    });

    this.form.controls.perfil.valueChanges.subscribe(
      (selectedValue: string | null) => {
        if (selectedValue === 'SUPERVISOR') {
          this.selectSupervisorEnabled = false;
          this.form.get('supervisor')?.clearValidators();
          this.form.get('supervisor')?.updateValueAndValidity();
          this.form.get('email')?.setValidators([Validators.required]);
          this.form.get('email')?.updateValueAndValidity();
        } else if (selectedValue == 'ATENDENTE') {
          this.selectSupervisorEnabled = true;
          this.form.get('supervisor')?.setValidators([Validators.required]);
          this.form.get('supervisor')?.updateValueAndValidity();
          this.form.get('email')?.clearValidators();
          this.form.get('email')?.updateValueAndValidity();
        } else {
          this.selectSupervisorEnabled = true;
          this.form.get('supervisor')?.setValidators([Validators.required]);
          this.form.get('supervisor')?.updateValueAndValidity();
          this.form.get('email')?.setValidators([Validators.required]);
          this.form.get('email')?.updateValueAndValidity();
        }
      }
    );

    markAllControlsAsDirty(this.form);

    this.populateRoleOptions();
    this.getSupervisors(null);
  }

  populateRoleOptions() {
    this.userService.getRoles().subscribe({
      next: (roles) => {
        this.roleOptions = roles;

        if (this.storageService.getRole() !== UserRoleEnum.supervisor)
          this.roleOptions = this.roleOptions.filter(
            (role) => role !== 'ATENDENTE'
          );
      },
    });
  }

  getSupervisors(name: string | null) {
    this.supervisorsFilter.pageNumber = 1;

    if (name) {
      this.supervisorsFilter.name = name;
      this.loadingSearchSupervisors = true;
    } else {
      this.loadingSupervisors = true;
      this.supervisorsFilter.name = null;
    }

    this.userService.get(this.supervisorsFilter).subscribe({
      next: (data) => {
        this.originalSupervisors = data.items;
        this.supervisors = data.items.map(
          (supervisor) => supervisor.name + ' | ' + supervisor.functional
        );
        this.totalItemsSupervisors = data.totalItems;
        this.loadingSupervisors = false;
        this.loadingSearchSupervisors = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os supervisores.');
        this.loadingSupervisors = false;
        this.loadingSearchSupervisors = false;
      },
    });
  }

  getMoreSupervisors() {
    if (this.totalItemsSupervisors != this.supervisors.length) {
      this.loadingMoreSupervisors = true;
      if (this.supervisorsFilter.pageNumber)
        this.supervisorsFilter.pageNumber += 1;

      this.userService.get(this.supervisorsFilter).subscribe({
        next: (data) => {
          this.originalSupervisors = this.originalSupervisors.concat(
            data.items
          );
          this.supervisors = this.supervisors.concat(
            data.items.map(
              (supervisor) => supervisor.name + ' | ' + supervisor.functional
            )
          );
          this.totalItemsSupervisors = data.totalItems;
          this.loadingMoreSupervisors = false;
        },
        error: () => {
          this.toastrService.error(
            'Não foi possível carregar os supervisores.'
          );
          this.loadingMoreSupervisors = false;
        },
      });
    }
  }

  private loadUserData() {
    this.loadingGet = true;
    this.userService.getById(this.userId).subscribe((user) => {
      if (user) {
        this.form.patchValue({
          name: user.name,
          email: user.email,
          functional: user.functional,
          perfil: user.role.name,
          supervisor: user.supervisor
            ? user.supervisor.functional + ' | ' + user.supervisor.name
            : null,
          active: user.active ? 'Ativo' : 'Inativo',
          recorderId: user.recorderId,
        });

        this.selectedSupervisors = user.supervisor
          ? [user.supervisor.functional + ' | ' + user.supervisor.name]
          : [];

        this.loadingGet = false;
      }
    });
  }

  update() {
    if (!this.form.valid || this.loadingUpdate) return;

    this.error = false;
    this.loadingUpdate = true;

    const request = {
      id: this.userId,
      name: this.form.controls.name.value ?? '',
      email: this.form.controls.email.value ?? '',
      functional: this.form.controls.functional.value ?? '',
      supervisorId:
        this.getSupervisorIdFromText(
          this.form.controls.supervisor.value ?? ''
        ) ?? null,
      roleName: this.form.controls.perfil.value ?? '',
      active: this.form.controls.active.value === 'Ativo',
      recorderId: this.form.controls.recorderId.value ?? null,
    } as UpdateUserRequest;

    if (request.roleName == 'ATENDENTE') {
      request.email = null;
    }

    if (request.roleName == 'SUPERVISOR') {
      request.supervisorId = null;
    }

    this.userService.updateUser(this.userId, request).subscribe({
      next: () => {
        this.loadingUpdate = false;
        this.toastrService.success('Usuário editado com sucesso!');
        this.form.reset();
        this.router.navigate(['/user']);
      },
      error: (e) => {
        this.loadingUpdate = false;
        this.error = true;
        this.toastrService.error(`Falha ao cadastrar usuário: ${e.error}`);
      },
    });
  }

  getSupervisorIdFromText(text: string): string | null {
    if (!text || text.trim() === '') return null;

    const [name, functionalString] = text.split('|').map((part) => part.trim());

    const supervisor = this.originalSupervisors.find(
      (attendant) =>
        attendant.functional.toString() === functionalString &&
        attendant.name.trim() === name.trim()
    );

    return supervisor ? supervisor.id : null;
  }

  handleNavigateBack() {
    this.form.reset();
    this.router.navigate(['/user/']);
  }
}
